import { useSortable } from '@dnd-kit/sortable';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MyStoreBundle } from '@1po/1po-bff-fe-spec/generated/my_store/bundles/response/GetMyStoreBundlesResponse';
import { MyStoreReference } from '@1po/1po-bff-fe-spec/generated/my_store/references/response/GetMyStoreReferencesResponse';
import { CSS } from '@dnd-kit/utilities';
import { ConfigProvider } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import { RootState } from 'app/AppStore';
import { AngleDownIcon, AngleRightIcon, Dots4Icon, PlusCircleIcon, TrashAltIcon } from 'assets/icons';
import { Dialog, useDisclosure } from 'components/Dialog';
import StopPropagation from 'components/StopPropagation/StopPropagation';
import { BUNDLES, MyStoreType, SectionTitleType } from 'domains/myStore/MyStore.types';
import { STable } from 'pages/EstimatePage/EstimatePage.styled';
import { SectionHeader } from 'pages/MyStorePage/Pages/Bundles/Bundles.styled';
import { ValidatedTextInput } from 'pages/MyStorePage/Pages/Bundles/BundlesTable/BundlesInput';
import { theme } from 'styles';
import { Box, Checkbox, ErrorButton, Flex, Icon, MarginBox, PaddingBox, Text, WhiteBlueButton } from 'UI';
import { useFocus } from 'utils';

const NoItemsMessage = ({ myStoreType }: { myStoreType: MyStoreType }) => {
  const { t } = useTranslation();

  return (
    <Flex justify={'center'}>
      <Text type="heading3">
        {myStoreType === BUNDLES
          ? t('my_store.bundles.no_bundles_in_category', 'There are no bundles available in this category.')
          : t('my_store.references.no_references_in_category', 'There are no references available in this category.')}
      </Text>
    </Flex>
  );
};

export const SelectBundles = ({
  entityId,
  enabled,
  handleOnChange,
  getIsSelectedSelector,
}: {
  entityId: string;
  enabled: boolean;
  handleOnChange: (v: boolean) => void;
  getIsSelectedSelector: (state: RootState, entityId: string) => boolean;
}) => {
  const isSelected = useSelector((state: RootState) => getIsSelectedSelector(state, entityId));

  return <Checkbox disabled={!enabled} checked={enabled && isSelected} onChange={handleOnChange} />;
};

const getSectionTitle = (t: TFunction, title: SectionTitleType, myStoreType: MyStoreType) => {
  switch (title) {
    case 'TIRES':
      return t('catalog.tires', 'Tires');
    case 'BRAKING':
      return t('catalog.brakes_category.braking', 'Braking');
    case 'MAINTENANCE':
      return t('catalog.parts.category.maintenance', 'Maintenance');
    case 'OTHER':
      return t('common.other', 'Other');
    case '':
      return myStoreType === BUNDLES
        ? t('common.new_bundle', 'New bundle category')
        : t('my_store.references.section.new_category', 'New local reference category');
    default:
      return title;
  }
};

const SectionTitle = ({
  sectionId,
  title,
  isEditing,
  setIsEditing,
  handleSectionTitleChange,
  myStoreType,
}: {
  sectionId: string;
  title: string;
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleSectionTitleChange: (sectionInd: string, title: string) => void;
  myStoreType: MyStoreType;
}) => {
  const { t } = useTranslation();
  const [titleState, setTitleState] = useState(getSectionTitle(t, title, myStoreType));
  const inputRef = useRef(null);

  useFocus(inputRef, [isEditing]);

  useEffect(() => {
    setTitleState(getSectionTitle(t, title, myStoreType));
  }, [t, title, myStoreType]);

  const handleTitleChange = (title: string) => {
    handleSectionTitleChange(sectionId, title);
    setTitleState(title);
  };

  return (
    <>
      {isEditing ? (
        <ValidatedTextInput
          noTooltip
          value={titleState}
          saveFunction={handleTitleChange}
          maxLength={window.innerWidth / 30}
          passRef={inputRef}
          validate={(v) => v.length > 0}
          onBlur={(v) => {
            setIsEditing(false);
            setTitleState(v);
          }}
          isLarge
        />
      ) : (
        <Text type="heading3" disableGutter onClick={() => setIsEditing(true)} noWrap cursor={'text'}>
          {titleState}
        </Text>
      )}
    </>
  );
};

const BundlesSection = ({
  sectionId,
  title,
  items,
  isDragging,
  myStoreType,
  handleAddItemInSection,
  handleDeleteSection,
  handleOnChangeSelection,
  handleSectionTitleChange,
  getIsSelectedSelector,
  columns,
  initiallyOpen = false,
}: {
  sectionId: string;
  title: string;
  items: MyStoreBundle[] | MyStoreReference[];
  isDragging: boolean;
  myStoreType: MyStoreType;
  initiallyOpen: boolean;
  columns: ColumnsType<MyStoreBundle | MyStoreReference>;
  handleAddItemInSection: () => void;
  handleDeleteSection: () => void;
  handleOnChangeSelection: (sectionInd: string, isSelected: boolean, itemId?: string) => void;
  handleSectionTitleChange: (sectionInd: string, title: string) => void;
  getIsSelectedSelector: (state: RootState, entityId: string) => boolean;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [showDelete, setShowDelete] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: sectionId });
  const disclosure = useDisclosure();
  const { onOpen } = disclosure;

  const handleOpenClose = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
        backgroundColor: theme.color.white,
        position: 'relative',
        zIndex: isDragging ? 200 : 100,
      }}
    >
      <Dialog
        title={t('common.dialog.actionRequired', 'Action required')}
        description={[
          t('my_store.bundles.remove.description1', 'You are about to delete all the category'),
          t('my_store.bundles.remove.description2', 'All lots associated to this category will be affected'),
        ]}
        icon={TrashAltIcon}
        handleConfirm={handleDeleteSection}
        disclosure={disclosure}
      />
      <Flex direction={'column'} gap={16}>
        <Flex gap={15} align={'center'}>
          <div ref={setNodeRef} {...attributes} {...listeners}>
            <Icon IconComponent={Dots4Icon} size={16} color={'black'} />
          </div>

          <SectionHeader
            align={'center'}
            gap={16}
            isOpen={isOpen}
            onClick={handleOpenClose}
            onMouseEnter={() => setShowDelete(true)}
            onMouseLeave={() => setShowDelete(false)}
          >
            {!isOpen && (
              <Flex align={'center'} gap={15} size={0} onClick={(e) => e.stopPropagation()}>
                <SelectBundles
                  entityId={sectionId}
                  enabled={true}
                  getIsSelectedSelector={getIsSelectedSelector}
                  handleOnChange={(isSelected) => handleOnChangeSelection(sectionId, isSelected)}
                />
                <Box height={48} width={1} background={theme.color.grey85} />
              </Flex>
            )}
            <Flex justify={'space-between'}>
              <Flex
                direction={isOpen ? 'row' : 'column'}
                align={isOpen ? 'center' : 'initial'}
                gap={isOpen ? 8 : 0}
                size={isTitleEditing ? 1 : 0}
                onClick={(e) => e.stopPropagation()}
              >
                <SectionTitle
                  sectionId={sectionId}
                  title={title}
                  isEditing={isTitleEditing}
                  setIsEditing={setIsTitleEditing}
                  handleSectionTitleChange={handleSectionTitleChange}
                  myStoreType={myStoreType}
                />
                <MarginBox mt={5}>
                  <Text type={'light_dimmer'} noWrap cursor={'text'} onClick={(e) => e.stopPropagation()}>
                    {myStoreType === BUNDLES
                      ? t('my_store.bundles.section.bundle_count', '{{count}} bundles', {
                          count: items.length,
                        })
                      : t('my_store.references.section.reference_count', '{{count}} references', {
                          count: items.length,
                        })}
                  </Text>
                </MarginBox>
              </Flex>
              <Flex size={0} gap={15} align={'center'} onClick={(e) => e.stopPropagation()}>
                {(isOpen || showDelete) && (
                  <ErrorButton size={'middle'} onClick={onOpen}>
                    <Flex gap={5} align={'center'}>
                      <Icon IconComponent={TrashAltIcon} size={16} />
                      {t('common.action.delete', 'Delete')}
                    </Flex>
                  </ErrorButton>
                )}
                <Icon IconComponent={isOpen ? AngleDownIcon : AngleRightIcon} size={20} onClick={handleOpenClose} />
              </Flex>
            </Flex>
          </SectionHeader>
        </Flex>
        {isOpen && (
          <StopPropagation>
            <PaddingBox pl={30}>
              <ConfigProvider renderEmpty={() => <NoItemsMessage myStoreType={myStoreType} />}>
                <STable<MyStoreBundle | MyStoreReference>
                  columns={columns}
                  dataSource={items}
                  pagination={false}
                  rowKey={myStoreType === BUNDLES ? 'id' : 'referenceId'}
                />
              </ConfigProvider>
              <WhiteBlueButton size={'large'} onClick={handleAddItemInSection}>
                <Flex gap={8} align={'center'}>
                  <Icon IconComponent={PlusCircleIcon} size={20} />
                  {myStoreType === BUNDLES
                    ? t('my_store.bundles.actions.add_bundle', 'Add bundle')
                    : t('my_store.references.actions.add_reference', 'Add other local reference')}
                </Flex>
              </WhiteBlueButton>
            </PaddingBox>
          </StopPropagation>
        )}
      </Flex>
    </div>
  );
};

export default BundlesSection;
